import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Banknote,
  BarChart3,
  Briefcase,
  Check,
  ChevronDown,
  ClipboardCheck,
  FileClock,
  HandCoins,
  FileSpreadsheet,
  FileText,
  FolderKanban,
  Globe,
  History,
  Inbox,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Menu,
  Package,
  Plus,
  Receipt,
  Settings,
  Users,
  X,
  type LucideIcon,
  Truck,
  Gauge,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore, useActiveOrg, type OrganisationSummary } from '@/stores/auth';
import { useProfile } from '@/hooks/useProfile';
import { usePermissions } from '@/hooks/usePermissions';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { ThemeToggle } from '@/features/common/ThemeToggle';
import { Button, ColourDot, FintMark } from '@/features/common/ui';
import { SearchPanel, SearchTrigger, useSearchShortcut } from '@/features/search/SearchPanel';
import { SearchBar } from '@/features/search/SearchBar';
import { TourHost } from '@/features/tour/TourHost';
import { TourHelpButton } from '@/features/tour/TourHelpButton';
import { NewBookSheet } from '@/features/books/NewBookSheet';
import { useInboxCount } from '@/features/inbox/useInboxCount';
import { cn } from '@/lib/cn';
import { LeavingBanner } from '@/features/settings/AccountPage';

interface NavItem {
  icon: LucideIcon;
  label: string;
  href: string;
  /** The permission that reveals it. A member never sees Banking at all. */
  resource?: string;
  /**
   * Only for a person who belongs to more than one business. The Overview is a
   * comparison, and a comparison of one thing is a dashboard with a worse
   * name - so for a single-org user it is not offered at all.
   */
  multiOrg?: boolean;
  /** Only where the AI CFO is offered (staff until its release - docs/roadmap.md). */
  cfoOnly?: boolean;
  /** Where the link goes when that is not `href` itself (the Overview carries the organisation). */
  to?: string;
  /**
   * A candidate for the phone's bottom bar.
   *
   * Marked rather than derived from position, because the two orders differ on
   * purpose. The SIDEBAR leads with the accountant's entries - a practising CA
   * opens the app to review and to file. The PHONE is the business owner
   * checking whether they were paid and photographing a receipt in a car park,
   * and giving them Clients / Review / GST returns as their four tabs would be
   * reading the sidebar's priorities onto a different person.
   */
  phone?: boolean;
  /**
   * The sidebar's section. Sixteen entries in one column read as a wall; the
   * owner asked for similar ones grouped with a separator. A heading on the
   * wide sidebar, a thin rule on the icon rail; the phone's tabs ignore it.
   */
  group: NavGroup;
  /** Drawn as an icon in the sidebar footer on a wide screen instead of a row. */
  footerOnWide?: boolean;
}

type NavGroup = 'home' | 'practice' | 'sales' | 'spend' | 'insight' | 'admin';

/** The heading over each section; the first and last have none - a rule is enough. */
const NAV_GROUP_LABEL: Record<NavGroup, string | null> = {
  home: null,
  practice: 'Accountant',
  sales: 'Sales',
  spend: 'Spend & bank',
  insight: 'Insight',
  admin: null,
};

/**
 * One list, rendered by the desktop sidebar and by the mobile bottom bar.
 *
 * Order is reading order and follows the money: what you are owed, then what
 * you owe, then where it sits.
 *
 * It also decides the phone: the first FOUR entries a person is permitted to
 * see become their bottom tabs, and everything else goes behind More. Deriving
 * the tabs rather than hardcoding them is what keeps the bar honest per role -
 * an accountant gets Clients and Review, an owner who cannot see them gets
 * Invoices and Customers instead, and neither is ever offered a destination
 * they would be refused at.
 */
const NAV_ITEMS: NavItem[] = [
  // Above the dashboard, because for the person who sees it at all it is
  // where they land. NOT a phone tab: the four below are the owner's day in a
  // car park, and the cross-business view is a sit-down read that More can
  // hold without anyone missing a receipt.
  { icon: LayoutGrid, label: 'Overview', href: '/overview', multiOrg: true, group: 'home' },
  { icon: LayoutDashboard, label: 'Dashboard', href: '/', phone: true, group: 'home' },
  // What Claude proposed and nobody has decided about - the one page that is a
  // to-do list, so it sits with the two a person opens the app on. The badge
  // is the count, because a proposal arrives from a chat window the person is
  // not looking at.
  { icon: Inbox, label: 'Inbox', href: '/inbox', group: 'home' },
  // The accountant's entry points, above the day-to-day ones: a practising CA
  // opens the app to review and to file, not to raise an invoice.
  { icon: Briefcase, label: 'Clients', href: '/clients', group: 'practice' },
  { icon: ClipboardCheck, label: 'Review', href: '/review', resource: 'review', group: 'practice' },
  { icon: FileSpreadsheet, label: 'GST returns', href: '/gst', resource: 'gstReturn', group: 'practice' },
  // Beside the returns and behind the same permission: the export register is
  // what follows Table 6A - the bank's monthly EDF and each invoice's money.
  { icon: Globe, label: 'Exports', href: '/exports', resource: 'gstReturn', group: 'practice' },
  { icon: FileText, label: 'Invoices', href: '/invoices', resource: 'invoice', phone: true, group: 'sales' },
  // Beside invoices rather than under them: a proforma is a different document
  // to a customer, and burying it as a tab would have people raising real tax
  // invoices to ask for an advance.
  { icon: FileClock, label: 'Proformas', href: '/proformas', resource: 'proforma', group: 'sales' },
  { icon: Users, label: 'Customers', href: '/customers', resource: 'customer', phone: true, group: 'sales' },
  // After customers rather than under invoices: a payment is against a
  // customer's account and may settle several invoices, or none.
  { icon: HandCoins, label: 'Payments', href: '/payments', resource: 'payment', group: 'sales' },
  // What an invoice is made of: the catalogue lives with what it is sold on.
  { icon: Package, label: 'Items', href: '/items', resource: 'item', group: 'sales' },
  { icon: Receipt, label: 'Expenses', href: '/expenses', resource: 'expense', phone: true, group: 'spend' },
  // Who you pay: Customers' other half, beside the spend it names.
  { icon: Truck, label: 'Vendors', href: '/vendors', resource: 'vendor', group: 'spend' },
  { icon: Banknote, label: 'Banking', href: '/banking', resource: 'banking', group: 'spend' },
  { icon: BarChart3, label: 'Reports', href: '/reports', resource: 'report', group: 'insight' },
  // The AI CFO's tab (docs/design/ai-cfo.md §4): Ask, Chase, Dues, Save, Debt.
  { icon: Gauge, label: 'CFO', href: '/cfo', resource: 'report', group: 'insight', cfoOnly: true },
  { icon: FolderKanban, label: 'Projects', href: '/projects', resource: 'project', group: 'insight' },
  // On the wide sidebar these two are icons in the footer, beside Sign out -
  // the row they freed is what lets the grouped list fit a laptop screen. The
  // icon rail and the phone keep them in the list.
  { icon: History, label: 'Activity', href: '/activity', resource: 'audit', group: 'admin', footerOnWide: true },
  { icon: Settings, label: 'Settings', href: '/settings', group: 'admin', footerOnWide: true },
];

export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { orgs, organisations, activeOrgId, clear, user } = useAuthStore();
  const switchTo = useSwitchOrg();
  const activeOrg = useActiveOrg();
  const onOverview = location.pathname === '/overview';
  /**
   * ── THE SHELVES ──
   * Books stand on organisations (0022). One organisation and nothing is
   * said about it: the switcher is the flat list of books it always was.
   * Two or more and a second, quieter control sits ABOVE the book switcher
   * naming the shelf, and the book switcher lists that shelf's books only -
   * two short menus, outer scope over inner, instead of one long grouped
   * one. It sits there and not top-right because the shelf contains the
   * book and the book contains the page: the sidebar reads downwards in
   * the order the scopes nest, the way a workspace sits over its channels.
   * Top-right, on the desktop, is the page's own actions; on a phone it
   * does not exist.
   *
   * The shelf shown is the active book's; on the Overview it is the shelf
   * being added up, which is the same shelf unless the address says
   * otherwise.
   */
  const activeOrganisation = organisations.find((o) => o.id === activeOrg?.organisationId);
  const shelfMenu = organisations.length > 1;
  const booksOn = (shelf: OrganisationSummary) => orgs.filter((o) => o.organisationId === shelf.id);
  const overviewShelfId = onOverview ? new URLSearchParams(location.search).get('organisation') : null;
  const currentShelf = (overviewShelfId ? organisations.find((o) => o.id === overviewShelfId) : undefined) ?? activeOrganisation;
  const menuBooks = shelfMenu && currentShelf ? booksOn(currentShelf) : orgs;
  /**
   * ── WHAT "ALL BOOKS" ADDS UP ──
   * The books of THIS organisation, and only those: a demo shelf's figures
   * never sit in the same total as the real ones, and the way to see
   * another organisation's books is to switch to it (Sai, 20 Sep). One
   * organisation, and it is every book, as it always was. A shelf with one
   * book has nothing to add up, so "All books" is not offered on it.
   */
  const overviewScope = shelfMenu && currentShelf ? currentShelf : null;
  const overviewBooks = overviewScope ? booksOn(overviewScope) : orgs;
  const overviewPath = overviewScope ? `/overview?organisation=${overviewScope.id}` : '/overview';
  const allBooksLabel = 'All books';
  const perms = usePermissions();
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const orgMenuRef = useRef<HTMLDivElement | null>(null);
  const [shelfMenuOpen, setShelfMenuOpen] = useState(false);
  const shelfMenuRef = useRef<HTMLDivElement | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  useSearchShortcut(openSearch);
  /** The shelf a new book goes on: the one chosen, else the current one if the person owns it. */
  const [newBookShelf, setNewBookShelf] = useState<string | null>(null);
  const [newBookOpen, setNewBookOpen] = useState(false);
  const inboxCount = useInboxCount();

  // Keeps the server as the authority on identity and permissions.
  useProfile();

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (orgMenuRef.current && !orgMenuRef.current.contains(e.target as Node)) setOrgMenuOpen(false);
      if (shelfMenuRef.current && !shelfMenuRef.current.contains(e.target as Node)) setShelfMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  // The More sheet closes on navigation. Without this, tapping a destination
  // inside it leaves the sheet covering the screen you just asked for.
  useEffect(() => setMoreOpen(false), [location.pathname]);

  const handleLogout = async () => {
    // Tell the server FIRST - it deletes the session row and clears the cookie.
    // Clearing only local state would leave the token valid for its full seven
    // days: a logout that logs nobody out. The local clear happens either way,
    // because leaving someone looking signed in is the worse outcome.
    await api.post('/auth/logout').catch(() => undefined);
    clear();
    queryClient.clear();
    navigate('/login');
  };

  /**
   * ── "ALL BOOKS" WHILE ON THE OVERVIEW ──
   * The Overview is every book at once, and a switcher that named one book
   * above it said something untrue. On /overview the control reads "All
   * books" with a stacked pair of dots, and the menu offers "All books" as
   * its first entry whenever there is more than one - picking a book from
   * there lands on that book's dashboard, as it always did.
   */
  const switcherLabel = onOverview && overviewBooks.length > 1 ? allBooksLabel : (activeOrg?.name ?? 'No book');

  const switchOrg = (orgId: string) => {
    setOrgMenuOpen(false);
    // The More sheet too: switching book while already on the Dashboard
    // does not change the path, so the path-change effect never closes it.
    setMoreOpen(false);
    // The cache clear lives in the hook - see useSwitchOrg for why it is the
    // step that must not be forgotten.
    switchTo(orgId);
  };

  const openNewBook = (shelfId: string | null) => {
    setOrgMenuOpen(false);
    setShelfMenuOpen(false);
    setMoreOpen(false);
    setNewBookShelf(shelfId);
    setNewBookOpen(true);
  };

  /**
   * Choosing a shelf lands where its books are: the shelf's Overview when
   * it holds two or more, the one book when it holds one, and the New book
   * sheet (already on that shelf) when it holds none the person owns. The
   * active book moves onto the shelf too, so every page under the sidebar
   * agrees with the name above the book switcher.
   */
  const goToShelf = (shelf: OrganisationSummary) => {
    setShelfMenuOpen(false);
    setMoreOpen(false);
    const books = booksOn(shelf);
    if (books.length === 0) {
      if (shelf.owner) openNewBook(shelf.id);
      return;
    }
    const target = books.find((b) => b.id === activeOrgId) ?? books[0];
    switchTo(target.id, books.length > 1 ? `/overview?organisation=${shelf.id}` : '/');
  };

  const shelfBookCount = (shelf: OrganisationSummary) => {
    const n = booksOn(shelf).length;
    return n === 0 ? 'No books yet' : n === 1 ? '1 book' : `${n} books`;
  };

  // The Overview is offered when there is something to add up on this shelf, and goes to this shelf.
  const visibleNav = NAV_ITEMS.filter(
    (item) =>
      (!item.resource || perms.can(item.resource, 'view')) &&
      (!item.multiOrg || overviewBooks.length > 1) &&
      (!item.cfoOnly || !!user?.cfo),
  ).map((item) => (item.href === '/overview' ? { ...item, to: overviewPath } : item));
  /**
   * Four tabs plus More.
   *
   * The `phone` ones this person is allowed to see, topped up from whatever
   * else they can reach if fewer than four survive - an accountant with no
   * invoice permission still gets four usable tabs rather than two and a gap.
   */
  const phonePicks = visibleNav.filter((item) => item.phone);
  const primaryNav = [...phonePicks, ...visibleNav.filter((item) => !item.phone)].slice(0, 4);
  const moreNav = visibleNav.filter((item) => !primaryNav.includes(item));
  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);
  /**
   * The quick-add entry: the biggest button in the chrome, because it is the
   * one thing the owner does every day. Offered to anyone who can record
   * either kind of money; the screen itself explains a tile they cannot use.
   */
  const canQuickAdd = perms.can('expense', 'create') || perms.can('banking', 'create');
  /**
   * ── TASK MODE ──
   * Quick add is a task, not a section: four screens with one decision each
   * and its own header with the way back. On a phone the shell's header and
   * tab bar come off around it, because the amount step opens with the
   * keyboard up and on an iPhone SE that leaves 268 points - the book name,
   * a step header, the amount box and the Next button are more than that
   * with the bar still there. The rail and sidebar stay: they are beside the
   * task, not on top of it.
   */
  const taskMode = location.pathname === '/add';
  const phoneTab = (item: NavItem) => (
    <Link
      key={item.href}
      to={item.to ?? item.href}
      aria-current={isActive(item.href) ? 'page' : undefined}
      className={cn(
        // `min-w-0`, or the label sets the tab's minimum width and five tabs
        // stop being fifths: "Dashboard" and "Customers" took 66px each on a
        // 320px screen and More was left 43px wide. `px-0.5` so that at 64px
        // a tab the longest label, "Customers", still fits without an
        // ellipsis.
        'flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1 px-0.5 text-[11px] active:bg-accent short:min-h-11',
        isActive(item.href) ? 'font-medium text-primary' : 'text-muted-foreground',
      )}
    >
      <item.icon className="size-5" aria-hidden />
      {/* `sr-only` on a short screen, not `hidden`: the word leaves the
          screen and stays in the name. An aria-label would do the same and
          also make the tab answer to getByLabel('Customer') from a form. */}
      <span className="max-w-full truncate short:sr-only">{item.label}</span>
    </Link>
  );

  return (
    /*
      ── THREE SHELLS, ONE ASIDE ──
      Below 640px: the phone. A header naming the book, a bottom bar with the
      Add button raised in the middle, everything else behind More.

      640px to 1023px: the RAIL. An iPad held upright is 768px, and a phone on
      its side is 734px to 863px; both used to get the phone shell or the
      full 240px sidebar, and both were wrong. The bottom bar on a 343px-tall
      landscape iPhone ate a third of the screen with the header; the sidebar
      on the iPad left 528px for an invoice table whose Total and Balance
      then sat behind a scroll. The rail is 80px of icons with their names
      under them - names, not tooltips, because the pointer here is a finger
      and there is no hover to reveal one - and the book switcher, theme and
      sign-out live in the same sheet the phone's More button opens, slid in
      from the left as the rail's flyout.

      1024px and up: the sidebar, unchanged.

      It is ONE <aside> and one set of links restyled at `lg:`, not two
      asides. Two would put every destination in the DOM twice, announced
      twice, and matched twice by every `getByRole('link')` in the suite.

      `h-dvh`, not `min-h-screen`, and for two reasons. 100vh on a phone is
      the height with the browser bar hidden, so the shell was taller than
      the screen while the bar was up; dvh is the height that is actually
      there. And a FIXED height is what makes <main> the thing that scrolls:
      with `min-h-` the document scrolled instead, the sidebar's Sign out
      ended up at the foot of a long page, and `position: sticky` inside the
      content - the quick-add footer - had a scroller ancestor that never
      scrolled and so never stuck. Print gets its height back so the page
      breaks where the paper does.
    */
    <div className="flex h-dvh bg-background pl-safe-left pr-safe-right print:h-auto">
      {/* ── Sidebar and rail ── */}
      {/* `print:hidden` on every piece of chrome: the invoice page is the
          document and is printed from the browser, and a navigation column
          down the left of a tax invoice is not part of the document. */}
      <aside className="hidden w-20 shrink-0 flex-col border-r border-border bg-card pt-safe-top print:hidden sm:flex lg:w-60">
        <div className="px-2 py-3 lg:px-4 lg:py-4">
          {/* The mark alone on the rail, the mark and the name where there is room. */}
          <Link to="/" className="flex items-center justify-center gap-2 text-lg font-semibold tracking-tight text-primary lg:justify-start touch:min-h-11" aria-label="Fint">
            <FintMark className="size-7" />
            <span className="hidden lg:inline">Fint</span>
          </Link>
          {/*
            ── THE BOOK SWITCHER ──
            The code says org; the screen says book. One login holds several
            of these - an LLP, a freelance practice, an accountant's clients -
            and calling them organisations made them feel like separate
            accounts. A book is what each one actually is: its own bank
            accounts, invoices, GST setting and returns, under one login. The
            identifiers (orgId, useOrg, x-org-id, the routes) are deliberately
            NOT renamed: the concept changed, the plumbing did not, and renaming
            plumbing across a live tree is how a wording change becomes a
            regression.
          */}
          {shelfMenu ? (
            <div className="relative hidden lg:mt-3 lg:block" ref={shelfMenuRef}>
              {/*
                Quieter than the book control under it - muted, no border -
                because it is the label over that control as much as it is a
                control itself: the shelf the book stands on.
              */}
              <button
                type="button"
                onClick={() => setShelfMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={shelfMenuOpen}
                aria-label={`Organisation: ${currentShelf?.name ?? 'none'}. Switch organisation`}
                data-testid="shelf-switcher"
                className="flex min-h-7 w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent touch:min-h-11"
              >
                {currentShelf ? <ColourDot colour={currentShelf.colour} className="h-1.5 w-1.5" /> : null}
                <span className="truncate">{currentShelf?.name ?? 'Organisation'}</span>
                <ChevronDown className="size-3 shrink-0" aria-hidden />
              </button>
              {shelfMenuOpen ? (
                <div role="menu" className="absolute left-0 right-0 z-20 mt-1 animate-slide-up overflow-hidden rounded-md border border-border bg-popover shadow-lg">
                  {organisations.map((shelf) => {
                    const empty = booksOn(shelf).length === 0;
                    return (
                      <button
                        key={shelf.id}
                        role="menuitem"
                        type="button"
                        onClick={() => goToShelf(shelf)}
                        disabled={empty && !shelf.owner}
                        data-testid="shelf-option"
                        className="flex min-h-9 w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent active:bg-accent disabled:opacity-60 disabled:hover:bg-transparent touch:min-h-11"
                      >
                        <span className="flex min-w-0 flex-col">
                          <span className="flex min-w-0 items-center gap-2">
                            <ColourDot colour={shelf.colour} className="h-2 w-2" />
                            <span className="truncate">{shelf.name}</span>
                          </span>
                          <span className="pl-4 text-xs text-muted-foreground">{shelfBookCount(shelf)}</span>
                        </span>
                        {shelf.id === currentShelf?.id ? <Check className="size-4 shrink-0 text-primary" aria-hidden /> : null}
                      </button>
                    );
                  })}
                  <Link
                    role="menuitem"
                    to="/settings/organisations"
                    onClick={() => setShelfMenuOpen(false)}
                    className="flex min-h-9 w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent touch:min-h-11"
                  >
                    <Settings className="size-4" aria-hidden />
                    Manage organisations
                  </Link>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className={cn('relative hidden lg:block', shelfMenu ? 'lg:mt-1' : 'lg:mt-3')} ref={orgMenuRef}>
            <button
              type="button"
              onClick={() => setOrgMenuOpen((o) => !o)}
              // border-input, not border-border: this is a control, and its
              // edge is the only thing that says so. The divider colour
              // measured 1.23:1 against the sidebar - a menu button you find
              // by knowing it is there.
              className="flex min-h-9 w-full items-center justify-between gap-2 rounded-md border border-input px-2.5 py-1.5 text-left text-sm hover:bg-accent active:bg-accent touch:min-h-11"
              aria-haspopup="menu"
              aria-expanded={orgMenuOpen}
            >
              {/*
                The book's colour, before its name, everywhere the name appears:
                here, in the menu, on the phone, on the Overview and on the
                Inbox. It is a find-aid for a person with two books that were
                both set up in one afternoon and are told apart by nothing else
                at a glance. The dot is aria-hidden; the name is the fact.
              */}
              <span className="flex min-w-0 items-center gap-2">
                {onOverview && overviewBooks.length > 1 ? (
                  <AllBooksDots orgs={overviewBooks} />
                ) : activeOrg ? (
                  <ColourDot colour={activeOrg.colour} className="h-2 w-2" />
                ) : null}
                <span className="truncate">{switcherLabel}</span>
              </span>
              <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            </button>
            {orgMenuOpen ? (
              <div
                role="menu"
                className="absolute left-0 right-0 z-20 mt-1 animate-slide-up overflow-hidden rounded-md border border-border bg-popover shadow-lg"
              >
                {overviewBooks.length > 1 ? (
                  <button
                    role="menuitem"
                    type="button"
                    onClick={() => {
                      setOrgMenuOpen(false);
                      navigate(overviewPath);
                    }}
                    className="flex min-h-9 w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left text-sm hover:bg-accent active:bg-accent touch:min-h-11"
                    data-testid="switcher-all-books"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <AllBooksDots orgs={overviewBooks} />
                      <span className="truncate">{allBooksLabel}</span>
                    </span>
                    {onOverview ? <Check className="size-4 text-primary" aria-hidden /> : null}
                  </button>
                ) : null}
                {menuBooks.map((org) => (
                  <button
                    key={org.id}
                    role="menuitem"
                    type="button"
                    onClick={() => switchOrg(org.id)}
                    className="flex min-h-9 w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent active:bg-accent touch:min-h-11"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <ColourDot colour={org.colour} className="h-2 w-2" />
                      <span className="truncate">{org.name}</span>
                    </span>
                    {org.id === activeOrgId && !onOverview ? <Check className="size-4 text-primary" aria-hidden /> : null}
                  </button>
                ))}
                {menuBooks.length === 0 ? <p className="px-3 py-2 text-xs text-muted-foreground">No books on this shelf yet</p> : null}
                {/*
                  In the switcher, because that is where a person looks for a
                  book that is not there. The only other way to get a second
                  book was to register a second account.
                */}
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => openNewBook(null)}
                  className="flex min-h-9 w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent touch:min-h-11"
                >
                  <Plus className="size-4" aria-hidden />
                  New book
                </button>
              </div>
            ) : null}
          </div>
          {/*
            The rail's book button. A 72px column cannot hold a dropdown, so
            it opens the sheet the phone's More button opens: the books, the
            theme, sign out. The dot is the book's colour at a size that
            reads from across a desk; the name under it is what the dot
            means.
          */}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            aria-label={`Book: ${switcherLabel}. Switch book, theme or sign out`}
            className="flex min-h-14 w-full flex-col items-center justify-center gap-1 rounded-md px-0.5 text-[10px] leading-tight text-muted-foreground hover:bg-accent active:bg-accent lg:hidden"
          >
            {onOverview && overviewBooks.length > 1 ? (
              <AllBooksDots orgs={overviewBooks} large />
            ) : activeOrg ? (
              <ColourDot colour={activeOrg.colour} className="h-4 w-4" />
            ) : null}
            <span className="flex max-w-full items-center gap-0.5">
              <span className="truncate">{switcherLabel}</span>
              <ChevronDown className="size-3 shrink-0" aria-hidden />
            </span>
          </button>
        </div>

        {canQuickAdd ? (
          <div className="px-2 pb-2">
            {/*
              A round button on the rail, a full-width one on the sidebar.
              The accessible name is the same either way; the word "Add" is
              only painted where there is room for it.
            */}
            <Link
              to="/add"
              aria-current={isActive('/add') ? 'page' : undefined}
              aria-label="Add money in or out"
              data-testid="quick-add-entry"
              className={cn(
                'mx-auto flex size-12 items-center justify-center gap-2 rounded-full bg-primary text-base font-medium text-primary-foreground shadow-md transition-colors hover:bg-primary/90 active:bg-primary/80',
                'lg:h-10 lg:w-full lg:rounded-md lg:shadow-none touch:lg:min-h-12',
              )}
            >
              <Plus className="size-5" aria-hidden />
              <span className="hidden lg:inline">Add</span>
            </Link>
          </div>
        ) : null}

        {/*
          Scrolls on its own when the screen is shorter than the list - a
          phone on its side is 343px tall and this is sixteen entries - so the
          footer with the theme toggle stays put and the page does not grow.
        */}
        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain px-2 pb-1" aria-label="Sections">
          {visibleNav.map((item, index) => (
            <Fragment key={item.href}>
            {index > 0 && visibleNav[index - 1].group !== item.group ? (
              <div className={cn('pt-0.5', item.footerOnWide && 'lg:hidden')} role="presentation" data-testid="nav-group">
                {NAV_GROUP_LABEL[item.group] ? (
                  <p className="hidden px-2.5 text-[10px] font-semibold uppercase leading-3 tracking-wider text-muted-foreground/80 lg:block">
                    {NAV_GROUP_LABEL[item.group]}
                  </p>
                ) : null}
                <hr className={cn('mx-3 mb-1 border-border', NAV_GROUP_LABEL[item.group] && 'lg:hidden')} />
              </div>
            ) : null}
            <Link
              key={item.href}
              to={item.to ?? item.href}
              // The phone bar had this and the sidebar did not: the tint and
              // the weight say "you are here" to the eye, and this says it to
              // a screen reader. Colour alone is not a signal.
              aria-current={isActive(item.href) ? 'page' : undefined}
              className={cn(
                item.footerOnWide && 'lg:hidden',
                // 48px tall and 80px wide: sixteen entries fit an upright iPad
                // without scrolling, and "GST returns" fits at 10px without
                // an ellipsis.
                'relative flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-md px-0.5 text-center text-[10px] leading-tight transition-colors',
                // 32px rows under a mouse: sixteen of them, the switcher, Add
                // and the footer fit a 1280x800 laptop without the list
                // scrolling, which at 36px it did - Activity and Settings sat
                // below the fold of the sidebar itself. Under a finger they
                // are 44px and the list scrolls on an iPad held sideways.
                // 28px with the section headings, so the whole list - headings
                // and all - still fits a 1280x800 laptop without scrolling.
                'lg:min-h-7 lg:flex-row lg:justify-start lg:gap-2.5 lg:px-2.5 lg:py-1 lg:text-left lg:text-sm touch:lg:min-h-11',
                isActive(item.href)
                  ? 'bg-primary/10 font-medium text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent',
              )}
            >
              <item.icon className="size-5 shrink-0 lg:size-4" aria-hidden />
              <span className="max-w-full truncate">{item.label}</span>
              {item.href === '/inbox' && inboxCount > 0 ? (
                <span
                  data-testid="inbox-badge"
                  className="absolute right-3 top-0.5 rounded-full bg-primary px-1.5 text-[11px] font-medium leading-5 text-primary-foreground lg:static lg:ml-auto"
                  aria-label={`${inboxCount} waiting`}
                >
                  {inboxCount}
                </span>
              ) : null}
            </Link>
            </Fragment>
          ))}
        </nav>

        <div className="border-t border-border p-2 pb-[max(0.5rem,var(--safe-bottom))]">
          <div className="flex items-center justify-center gap-2 px-1 py-1 lg:justify-between lg:px-1.5">
            <span className="hidden min-w-0 flex-1 truncate text-xs text-muted-foreground lg:block" title={user?.email}>
              {user?.name ?? ''}
            </span>
            {visibleNav
              .filter((item) => item.footerOnWide)
              .map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  aria-label={item.label}
                  title={item.label}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                  className={cn(
                    'hidden size-8 shrink-0 items-center justify-center rounded-md hover:bg-accent hover:text-foreground lg:flex touch:size-11',
                    isActive(item.href) ? 'bg-primary/10 text-primary' : 'text-muted-foreground',
                  )}
                >
                  <item.icon className="size-4" aria-hidden />
                </Link>
              ))}
            <ThemeToggle />
            {/* An icon beside the theme switch rather than a row of its own:
                the row is what let the grouped list fit a laptop screen. */}
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Sign out"
              title="Sign out"
              className="hidden size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground lg:flex touch:size-11"
            >
              <LogOut className="size-4" aria-hidden />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          The phone header carries the BUSINESS NAME, not the product name.
          "Fint" tells you nothing you did not know; which client's books you
          are looking at is the one thing an accountant with fifteen of them
          needs on every screen, and the old mobile bar showed it nowhere.

          `pt-safe-top`: installed, this row is the top of the screen, and
          without the inset the name sits behind the clock and the notch.
          Hidden on a short screen - a phone on its side under 640px - where
          it and the bar together were a third of the height; the More sheet
          still names the book.
        */}
        <header
          className={cn(
            'flex items-center justify-between gap-2 border-b border-border bg-card px-4 py-2.5 pt-[max(0.625rem,var(--safe-top))] print:hidden short:hidden sm:hidden',
            taskMode && 'hidden',
          )}
        >
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex min-h-11 min-w-0 flex-1 items-center gap-1.5 rounded-md text-left active:bg-accent"
            aria-haspopup="dialog"
          >
            {onOverview && overviewBooks.length > 1 ? (
              <AllBooksDots orgs={overviewBooks} />
            ) : activeOrg ? (
              <ColourDot colour={activeOrg.colour} className="h-2 w-2" />
            ) : null}
            <span className="truncate text-base font-semibold">
              {onOverview && overviewBooks.length > 1 ? allBooksLabel : (activeOrg?.name ?? 'Fint')}
            </span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          </button>
          <SearchTrigger variant="icon" onOpen={openSearch} />
          <TourHelpButton />
          <ThemeToggle />
        </header>
        <SearchPanel open={searchOpen} onClose={closeSearch} />
        {/*
          The search bar, across the top of every page from a tablet up - a box
          typed into where it is, not a button that opens one. The phone keeps
          its icon in the header above and the full-screen sheet.
        */}
        <div
          className={cn(
            'hidden items-center justify-center border-b border-border bg-card px-5 py-2 print:hidden sm:flex',
            taskMode && 'sm:hidden',
          )}
        >
          <SearchBar />
          <TourHelpButton className="ml-2 shrink-0" />
        </div>
        <TourHost />

        {/*
          Content, with room at the bottom for the tab bar.
          Padding on the scroller rather than a margin on the page: the bar is
          fixed, so without it the last row of every list sits underneath it.
          The room is the bar's height PLUS the home-indicator inset the bar
          itself is padded by, and it is only needed where the bar is.
        */}
        <main
          className={cn(
            'min-w-0 flex-1 overflow-y-auto print:overflow-visible print:pb-0',
            !taskMode && 'pb-[calc(4rem+var(--safe-bottom))] short:pb-[calc(2.75rem+var(--safe-bottom))] sm:pb-0',
          )}
        >
          {/* The read-only seat says so, once, on every screen: every write
              control is already hidden by its permission, and this is for
              the person wondering why. */}
          {activeOrg?.roleKey === 'auditor' && !onOverview ? (
            <p
              className="mx-4 mt-3 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200 sm:mx-5 print:hidden"
              data-testid="auditor-banner"
            >
              You are viewing <strong>{activeOrg.name}</strong> as an auditor. Nothing can be changed from this account.
            </p>
          ) : null}
          <LeavingBanner />
          <Outlet />
        </main>

        {/*
          ── THE PHONE NAVIGATION ──
          It was a horizontally scrolling strip of all twelve destinations, cut
          off at both edges: the first item read "ew" and the last was half an
          icon. Nothing said what was off-screen, Dashboard was not visible at
          all, and there was no org switcher anywhere on a phone.

          A bottom bar because that is where a thumb is. Four tabs and More,
          because five is what fits at a tap size worth having.

          `pb-safe-bottom`: on an iPhone the home indicator is drawn over the
          bottom 34px of the screen, and a bar that ends at the screen's edge
          has its labels under the indicator and its taps competing with the
          swipe-home gesture. On a short screen the labels go and the bar
          drops to 44px - still a thumb target, no longer a third of the
          screen.
        */}
        <nav
          className={cn(
            'fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-card pb-safe-bottom pl-safe-left pr-safe-right print:hidden sm:hidden',
            taskMode && 'hidden',
          )}
          aria-label="Sections"
        >
          {/*
            The Add button in the CENTRE of the bar, raised, where a thumb
            lands without looking - two tabs either side of it. It is not a
            tab: it opens a task, not a section, and it looks like a button
            so that a person who has never seen the app taps it first.
          */}
          {(canQuickAdd ? primaryNav.slice(0, 2) : primaryNav).map(phoneTab)}
          {canQuickAdd ? (
            <Link
              to="/add"
              aria-label="Add money in or out"
              aria-current={isActive('/add') ? 'page' : undefined}
              data-testid="quick-add-entry-phone"
              className="flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center px-1 text-[11px] text-primary short:min-h-11"
            >
              <span className="-mt-5 flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-colors active:bg-primary/80 short:mt-0 short:size-9">
                <Plus className="size-6" aria-hidden />
              </span>
              <span className="mt-0.5 font-medium short:sr-only">Add</span>
            </Link>
          ) : null}
          {canQuickAdd ? primaryNav.slice(2).map(phoneTab) : null}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            className={cn(
              'flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 text-[11px] active:bg-accent short:min-h-11',
              moreOpen || moreNav.some((i) => isActive(i.href))
                ? 'font-medium text-primary'
                : 'text-muted-foreground',
            )}
          >
            <Menu className="size-5" aria-hidden />
            <span className="short:sr-only">More</span>
          </button>
        </nav>

        {/*
          ── THE MORE SHEET, AND THE RAIL'S FLYOUT ──
          On a phone it rises from the bottom, under the thumb that opened it.
          Between 640px and 1023px it is the rail's flyout instead: a panel
          down the left edge, beside the rail that opened it, holding what the
          rail has no room for - the book switcher, the theme, sign out. The
          destinations grid is only for the phone, where four of them are
          tabs and the rest are here; the rail shows them all.
        */}
        {moreOpen ? (
          <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-label="More">
            <button
              type="button"
              aria-label="Close"
              className="absolute inset-0 bg-black/40"
              onClick={() => setMoreOpen(false)}
            />
            <div
              data-sheet-panel
              className={cn(
                'absolute inset-x-0 bottom-0 max-h-[85dvh] animate-slide-up overflow-y-auto overscroll-contain rounded-t-xl border-t border-border bg-card pb-safe-bottom pl-safe-left pr-safe-right',
                'sm:inset-y-0 sm:right-auto sm:w-80 sm:max-h-none sm:rounded-none sm:border-r sm:border-t-0 sm:pt-safe-top',
              )}
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <span className="text-sm font-semibold">More</span>
                <Button variant="ghost" size="icon" aria-label="Close" onClick={() => setMoreOpen(false)}>
                  <X className="size-5" />
                </Button>
              </div>

              {/*
                The book switcher, which had no home on a phone at all. An
                accountant holding several clients could not tell which book
                they were in, let alone change it.
              */}
              {/*
                Shown with one book too, now - it is where "New book" lives,
                and a person with one book is the person who needs it.
              */}
              {shelfMenu ? (
                <div className="border-b border-border px-4 py-3">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Organisation</p>
                  <div className="space-y-1">
                    {organisations.map((shelf) => {
                      const empty = booksOn(shelf).length === 0;
                      return (
                        <button
                          key={shelf.id}
                          type="button"
                          onClick={() => goToShelf(shelf)}
                          disabled={empty && !shelf.owner}
                          data-testid="shelf-option-sheet"
                          className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-2 text-left text-sm hover:bg-accent active:bg-accent disabled:opacity-60"
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <ColourDot colour={shelf.colour} className="h-2 w-2" />
                            <span className="truncate">{shelf.name}</span>
                            <span className="shrink-0 text-xs text-muted-foreground">{shelfBookCount(shelf)}</span>
                          </span>
                          {shelf.id === currentShelf?.id ? <Check className="size-4 shrink-0 text-primary" aria-hidden /> : null}
                        </button>
                      );
                    })}
                    <Link
                      to="/settings/organisations"
                      className="flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent"
                    >
                      <Settings className="size-4 shrink-0" aria-hidden />
                      Manage organisations
                    </Link>
                  </div>
                </div>
              ) : null}
              <div className="border-b border-border px-4 py-3">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Book
                </p>
                <div className="space-y-1">
                  {overviewBooks.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setMoreOpen(false);
                        navigate(overviewPath);
                      }}
                      className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-2 text-left text-sm hover:bg-accent active:bg-accent"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <AllBooksDots orgs={overviewBooks} />
                        <span className="truncate">{allBooksLabel}</span>
                      </span>
                      {onOverview ? <Check className="size-4 shrink-0 text-primary" aria-hidden /> : null}
                    </button>
                  ) : null}
                  {menuBooks.map((org) => (
                    <button
                      key={org.id}
                      type="button"
                      onClick={() => switchOrg(org.id)}
                      className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-2 text-left text-sm hover:bg-accent active:bg-accent"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <ColourDot colour={org.colour} className="h-2 w-2" />
                        <span className="truncate">{org.name}</span>
                      </span>
                      {org.id === activeOrgId && !onOverview ? (
                        <Check className="size-4 shrink-0 text-primary" aria-hidden />
                      ) : null}
                    </button>
                  ))}
                  {menuBooks.length === 0 ? <p className="px-2 py-2 text-xs text-muted-foreground">No books on this shelf yet</p> : null}
                  <button
                    type="button"
                    onClick={() => openNewBook(null)}
                    className="flex min-h-11 w-full items-center gap-2 rounded-md px-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent"
                  >
                    <Plus className="size-4 shrink-0" aria-hidden />
                    New book
                  </button>
                </div>
              </div>

              <nav className="grid grid-cols-2 gap-1 p-3 sm:hidden">
                {moreNav.map((item) => (
                  <Link
                    key={item.href}
                    to={item.to ?? item.href}
                    className={cn(
                      'flex min-h-12 items-center gap-2.5 rounded-md px-3 text-sm',
                      isActive(item.href)
                        ? 'bg-primary/10 font-medium text-primary'
                        : 'text-foreground hover:bg-accent active:bg-accent',
                    )}
                  >
                    <item.icon className="size-4 shrink-0" aria-hidden />
                    <span className="truncate">{item.label}</span>
                    {item.href === '/inbox' && inboxCount > 0 ? (
                      <span className="ml-auto rounded-full bg-primary px-1.5 text-[11px] font-medium leading-5 text-primary-foreground">
                        {inboxCount}
                      </span>
                    ) : null}
                  </Link>
                ))}
              </nav>

              <div className="border-t border-border px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm">{user?.name ?? ''}</p>
                    <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  {/*
                    The theme, here as well as in the header: on a short
                    screen the header is gone, and this is the one place a
                    phone on its side can still flip it.
                  */}
                  <ThemeToggle className="sm:hidden" />
                </div>
                <Button variant="outline" className="mt-3 min-h-11 w-full" onClick={handleLogout}>
                  <LogOut className="mr-2 size-4" aria-hidden />
                  Sign out
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {newBookOpen ? (
        <NewBookSheet
          onClose={() => setNewBookOpen(false)}
          organisationId={newBookShelf ?? (currentShelf?.owner ? currentShelf.id : undefined)}
        />
      ) : null}
    </div>
  );
}

/** The standard page header: title, optional subtitle, optional actions. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    /*
      ── TIGHTER ON A PHONE ──
      This header was costing about 200px before any data: a title, a subtitle,
      and the action wrapping onto its own full-width line because
      `flex-wrap` had nowhere to put it. On the invoice list that plus the
      search box and the filter chips meant the first invoice began below the
      fold - on the screen whose entire job is to show invoices.

      So: the action stays BESIDE the title rather than wrapping under it
      the subtitle is hidden on a phone. It explains a screen you have already
      navigated to and whose name is at the top; it earns its space on a laptop
      and not on 390 pixels.

      The actions still WRAP rather than being forced beside the title. Forcing
      them inline truncated the composer's heading to "New in…", because that
      screen has three of them; wrapping keeps a single action inline where it
      fits and moves three onto their own line where they do not.
    */
    <div className="flex flex-wrap items-start justify-between gap-2 px-4 pb-3 pt-4 sm:gap-3 sm:px-5 sm:pb-4 sm:pt-5">
      <div className="min-w-0 space-y-1">
        <h1 className="text-lg font-semibold tracking-tight sm:text-xl">{title}</h1>
        {subtitle ? <p className="hidden text-sm text-muted-foreground sm:block">{subtitle}</p> : null}
      </div>
      {/*
        `max-w-full` and `flex-wrap`, NOT `shrink-0`. With shrink-0 the
        Inbox's three actions were one 450px-wide item: on a phone it wrapped
        under the title and then ran off the right edge, taking "Confirm 3
        selected" - the button the screen exists for - with it, and on an
        iPad it made the whole page 66px wider than the screen. Now the
        group wraps under the title when it must and its buttons wrap inside
        it when even that is not enough.
      */}
      {actions ? <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * Every book's colour at once - the mark for "All books". Up to four dots,
 * overlapping, in the switcher's order; more than four reads as a row of
 * confetti and says nothing the first four do not. aria-hidden: the label
 * beside it carries the words.
 */
function AllBooksDots({ orgs, large = false }: { orgs: Array<{ id: string; colour: string }>; large?: boolean }) {
  const size = large ? 'h-4 w-4' : 'h-2 w-2';
  return (
    <span className="flex shrink-0 items-center" aria-hidden data-testid="all-books-dots">
      {orgs.slice(0, 4).map((org, i) => (
        <ColourDot key={org.id} colour={org.colour} className={cn(size, i > 0 && (large ? '-ml-1.5' : '-ml-0.5'))} />
      ))}
    </span>
  );
}
